import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useReseller } from "../context/ResellerContext";
import {
  Users,
  LogOut,
  LayoutDashboard,
  UserPlus,
  Wallet,
  Settings,
  TrendingUp,
  Copy,
  CheckCircle,
  Clock,
  IndianRupee,
  Building2,
  Calendar,
  ArrowUpRight,
  Loader2,
  CreditCard,
  Banknote,
  Award,
  Target,
  Link as LinkIcon,
  ExternalLink,
  FileText,
  XCircle,
  RefreshCw,
  Bell,
  Sparkles,
  PartyPopper,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  bronze: "bg-orange-100 text-orange-700",
  silver: "bg-gray-200 text-gray-700",
  gold: "bg-yellow-100 text-yellow-700",
  platinum: "bg-purple-100 text-purple-700"
};

const TIER_RATES = {
  bronze: 15,
  silver: 17,
  gold: 19,
  platinum: 20
};

export default function ResellerDashboard() {
  const navigate = useNavigate();
  const { reseller, logout, updateProfile, refreshProfile, isAuthenticated, loading: authLoading } = useReseller();
  
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Payout request state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: "",
    payout_method: "bank_transfer",
    notes: ""
  });
  const [payoutLoading, setPayoutLoading] = useState(false);
  
  // Settings state
  const [settingsForm, setSettingsForm] = useState({
    bank_name: "",
    bank_account: "",
    bank_ifsc: "",
    upi_id: ""
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Commission notification state
  const [lastCommissionCount, setLastCommissionCount] = useState(0);
  const [newCommission, setNewCommission] = useState(null);
  const [showCommissionPopup, setShowCommissionPopup] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/reseller/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboard();
      fetchReferrals();
      fetchCommissions();
      fetchPayouts();
      
      // Poll for new commissions every 30 seconds
      const pollInterval = setInterval(() => {
        checkForNewCommissions();
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [isAuthenticated]);

  // Check for new commissions (polling)
  const checkForNewCommissions = async () => {
    try {
      const response = await axios.get(`${API}/reseller/commissions`);
      const newCommissions = response.data;
      
      // If we have more commissions than before, show notification
      if (lastCommissionCount > 0 && newCommissions.length > lastCommissionCount) {
        const latestCommission = newCommissions[0]; // Most recent
        setNewCommission(latestCommission);
        setShowCommissionPopup(true);
        
        // Show toast notification
        toast.success(
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <PartyPopper className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold">Commission Earned!</p>
              <p className="text-sm">₹{latestCommission.commission_amount?.toLocaleString()} from {latestCommission.tenant_name}</p>
            </div>
          </div>,
          { duration: 8000 }
        );
        
        // Refresh dashboard stats
        fetchDashboard();
        setCommissions(newCommissions);
      }
      
      setLastCommissionCount(newCommissions.length);
    } catch (error) {
      console.error("Failed to check for new commissions:", error);
    }
  };

  useEffect(() => {
    if (reseller) {
      setSettingsForm({
        bank_name: reseller.bank_name || "",
        bank_account: reseller.bank_account || "",
        bank_ifsc: reseller.bank_ifsc || "",
        upi_id: reseller.upi_id || ""
      });
    }
  }, [reseller]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/reseller/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/reseller/referrals`);
      setReferrals(response.data);
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
    }
  };

  const fetchCommissions = async () => {
    try {
      const response = await axios.get(`${API}/reseller/commissions`);
      setCommissions(response.data);
      // Initialize the count on first load
      if (lastCommissionCount === 0) {
        setLastCommissionCount(response.data.length);
      }
    } catch (error) {
      console.error("Failed to fetch commissions:", error);
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await axios.get(`${API}/reseller/payouts`);
      setPayouts(response.data);
    } catch (error) {
      console.error("Failed to fetch payouts:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/reseller/login");
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/signup?partner=${reseller?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRequestPayout = async () => {
    if (!payoutForm.amount || parseFloat(payoutForm.amount) < 500) {
      toast.error("Minimum payout amount is ₹500");
      return;
    }

    if (parseFloat(payoutForm.amount) > (dashboard?.pending_payout || 0)) {
      toast.error("Insufficient balance");
      return;
    }

    setPayoutLoading(true);
    try {
      await axios.post(`${API}/reseller/payouts/request`, {
        amount: parseFloat(payoutForm.amount),
        payout_method: payoutForm.payout_method,
        notes: payoutForm.notes
      });
      toast.success("Payout request submitted!");
      setShowPayoutModal(false);
      setPayoutForm({ amount: "", payout_method: "bank_transfer", notes: "" });
      fetchDashboard();
      fetchPayouts();
      refreshProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request payout");
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await updateProfile(settingsForm);
      toast.success("Settings saved!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!reseller) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900">Reseller Portal</span>
                <p className="text-xs text-gray-600">aftersales.pro</p>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
                  { id: "referrals", icon: UserPlus, label: "Referrals" },
                  { id: "earnings", icon: IndianRupee, label: "Earnings" },
                  { id: "payouts", icon: Wallet, label: "Payouts" },
                  { id: "settings", icon: Settings, label: "Settings" },
                ].map((tab) => (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab(tab.id)}
                    className={activeTab === tab.id ? "bg-white shadow-sm text-gray-900" : "text-gray-600 hover:text-gray-900"}
                  >
                    <tab.icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </Button>
                ))}
              </div>
              <Button
                variant="ghost"
                className="text-gray-600 hover:text-gray-900"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center gap-2">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-[130px] bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="dashboard">Dashboard</SelectItem>
                  <SelectItem value="referrals">Referrals</SelectItem>
                  <SelectItem value="earnings">Earnings</SelectItem>
                  <SelectItem value="payouts">Payouts</SelectItem>
                  <SelectItem value="settings">Settings</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-1">Welcome, {reseller.name}!</h1>
              <div className="flex items-center gap-2">
                <Badge className={`${TIER_COLORS[dashboard?.tier || "bronze"]} border-0`}>
                  <Award className="w-3 h-3 mr-1" />
                  {(dashboard?.tier || "bronze").charAt(0).toUpperCase() + (dashboard?.tier || "bronze").slice(1)} Partner
                </Badge>
                <span className="text-purple-200">•</span>
                <span className="text-sm text-purple-200">{dashboard?.commission_rate || 15}% Commission Rate</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <p className="text-xs text-purple-200">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-lg">{reseller.referral_code}</span>
                  <button onClick={copyReferralLink} className="hover:bg-white/20 rounded p-1">
                    {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{dashboard?.total_referrals || 0}</p>
                      <p className="text-xs text-gray-600">Total Referrals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{dashboard?.successful_conversions || 0}</p>
                      <p className="text-xs text-gray-600">Conversions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">₹{(dashboard?.total_earnings || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-600">Total Earned</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">₹{(dashboard?.pending_payout || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-600">Available Balance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* This Month & Referral Link */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    This Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-gray-900">{dashboard?.month_referrals || 0}</p>
                      <p className="text-sm text-gray-600">Referrals</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900">{dashboard?.month_conversions || 0}</p>
                      <p className="text-sm text-gray-600">Conversions</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-green-600">₹{(dashboard?.month_earnings || 0).toLocaleString()}</p>
                      <p className="text-sm text-gray-600">Earnings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Your Referral Link
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between gap-2">
                    <code className="text-sm text-gray-700 truncate flex-1">
                      {window.location.origin}/signup?partner={reseller.referral_code}
                    </code>
                    <Button size="sm" onClick={copyReferralLink} className="bg-purple-600 hover:bg-purple-700">
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 mt-3">
                    Share this link to earn {dashboard?.commission_rate || 15}% commission on every paid subscription
                  </p>
                  {/* WhatsApp Share Button */}
                  <Button 
                    onClick={() => {
                      const text = `Hey! I'm using AfterSales.pro for my repair shop management. Sign up with my referral link and get started: ${window.location.origin}/signup?partner=${reseller.referral_code}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                    className="w-full mt-3 bg-green-500 hover:bg-green-600"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Share on WhatsApp
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Request Payout CTA */}
            {(dashboard?.pending_payout || 0) >= 500 && (
              <Card className="bg-gradient-to-r from-green-500 to-emerald-600 border-0">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="text-white">
                    <h3 className="text-xl font-bold mb-1">Ready to Withdraw!</h3>
                    <p className="text-green-100">You have ₹{(dashboard?.pending_payout || 0).toLocaleString()} available for payout</p>
                  </div>
                  <Button onClick={() => setShowPayoutModal(true)} className="bg-white text-green-600 hover:bg-green-50">
                    <Wallet className="w-4 h-4 mr-2" />
                    Request Payout
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Recent Commissions */}
            {commissions.length > 0 && (
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    Recent Commissions
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("earnings")}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {commissions.slice(0, 3).map((comm, index) => {
                      const isNew = index === 0 && new Date(comm.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
                      return (
                        <div key={comm.id} className={`flex items-center justify-between p-3 rounded-lg ${isNew ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isNew ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <TrendingUp className={`w-5 h-5 ${isNew ? 'text-green-600' : 'text-gray-600'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">{comm.tenant_name || "Shop"}</p>
                                {isNew && (
                                  <Badge className="bg-green-500 text-white text-xs px-1.5 py-0">NEW</Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {comm.plan_name} • {new Date(comm.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${isNew ? 'text-green-600' : 'text-gray-900'}`}>
                              +₹{comm.commission_amount?.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">{comm.commission_rate}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Your Referrals</h2>
              <Button variant="outline" onClick={fetchReferrals}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {referrals.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <UserPlus className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Referrals Yet</h3>
                  <p className="text-gray-600 mb-4">Share your referral link to start earning commissions</p>
                  <Button onClick={copyReferralLink} className="bg-purple-600 hover:bg-purple-700">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Referral Link
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {referrals.map((ref) => (
                  <Card key={ref.id || ref.tenant_id} className="bg-white border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            ref.status === "converted" ? "bg-green-100" : "bg-yellow-100"
                          }`}>
                            <Building2 className={`w-5 h-5 ${
                              ref.status === "converted" ? "text-green-600" : "text-yellow-600"
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{ref.tenant_name || "Shop"}</p>
                            <p className="text-sm text-gray-500">
                              Referred on {new Date(ref.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            ref.status === "converted" ? "bg-green-100 text-green-700" :
                            ref.status === "signed_up" ? "bg-blue-100 text-blue-700" :
                            "bg-yellow-100 text-yellow-700"
                          }>
                            {ref.status === "converted" ? "Converted" :
                             ref.status === "signed_up" ? "Signed Up" : "Pending"}
                          </Badge>
                          <p className="text-sm text-gray-500 mt-1">
                            {ref.subscription_status || "Free"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === "earnings" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Commission History</h2>
              <Button variant="outline" onClick={fetchCommissions}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {commissions.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <IndianRupee className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Commissions Yet</h3>
                  <p className="text-gray-600">You'll earn commissions when your referrals subscribe to a paid plan</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {commissions.map((comm) => (
                  <Card key={comm.id} className="bg-white border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{comm.tenant_name || "Shop"}</p>
                            <p className="text-sm text-gray-500">
                              {comm.plan_name} • {new Date(comm.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">+₹{comm.commission_amount?.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">
                            {comm.commission_rate}% of ₹{comm.payment_amount?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === "payouts" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Payout History</h2>
                <p className="text-sm text-gray-500">Available Balance: ₹{(dashboard?.pending_payout || 0).toLocaleString()}</p>
              </div>
              <Button 
                onClick={() => setShowPayoutModal(true)}
                disabled={(dashboard?.pending_payout || 0) < 500}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Request Payout
              </Button>
            </div>

            {payouts.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <Wallet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Payouts Yet</h3>
                  <p className="text-gray-600">Request a payout when you have at least ₹500 available</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {payouts.map((payout) => (
                  <Card key={payout.id} className="bg-white border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            payout.status === "completed" ? "bg-green-100" :
                            payout.status === "rejected" ? "bg-red-100" :
                            payout.status === "processing" ? "bg-blue-100" :
                            "bg-yellow-100"
                          }`}>
                            {payout.status === "completed" ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                             payout.status === "rejected" ? <XCircle className="w-5 h-5 text-red-600" /> :
                             payout.status === "processing" ? <Clock className="w-5 h-5 text-blue-600" /> :
                             <Clock className="w-5 h-5 text-yellow-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">₹{payout.amount.toLocaleString()}</p>
                            <p className="text-sm text-gray-500">
                              {payout.payout_method === "bank_transfer" ? "Bank Transfer" : "UPI"} • {new Date(payout.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          payout.status === "completed" ? "bg-green-100 text-green-700" :
                          payout.status === "rejected" ? "bg-red-100 text-red-700" :
                          payout.status === "processing" ? "bg-blue-100 text-blue-700" :
                          "bg-yellow-100 text-yellow-700"
                        }>
                          {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                        </Badge>
                      </div>
                      {payout.admin_notes && (
                        <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded">
                          Note: {payout.admin_notes}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-900">Account Settings</h2>

            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payout Details
                </CardTitle>
                <p className="text-sm text-gray-500">Add your bank details to receive payouts</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={settingsForm.bank_name}
                      onChange={(e) => setSettingsForm({ ...settingsForm, bank_name: e.target.value })}
                      placeholder="e.g., HDFC Bank"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={settingsForm.bank_account}
                      onChange={(e) => setSettingsForm({ ...settingsForm, bank_account: e.target.value })}
                      placeholder="Account number"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={settingsForm.bank_ifsc}
                      onChange={(e) => setSettingsForm({ ...settingsForm, bank_ifsc: e.target.value.toUpperCase() })}
                      placeholder="e.g., HDFC0001234"
                      className="bg-gray-50 border-gray-300 uppercase"
                    />
                  </div>
                  <div>
                    <Label>UPI ID (Alternative)</Label>
                    <Input
                      value={settingsForm.upi_id}
                      onChange={(e) => setSettingsForm({ ...settingsForm, upi_id: e.target.value })}
                      placeholder="yourname@upi"
                      className="bg-gray-50 border-gray-300"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleSaveSettings}
                  disabled={settingsLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {settingsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Details"
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Profile Info */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{reseller.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{reseller.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{reseller.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <Badge className={
                      reseller.status === "active" ? "bg-green-100 text-green-700" :
                      reseller.status === "suspended" ? "bg-red-100 text-red-700" :
                      "bg-yellow-100 text-yellow-700"
                    }>
                      {reseller.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tier Info */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Partner Tier
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {Object.entries(TIER_RATES).map(([tier, rate]) => (
                    <div 
                      key={tier}
                      className={`p-4 rounded-lg border-2 text-center ${
                        dashboard?.tier === tier 
                          ? "border-purple-500 bg-purple-50" 
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <Badge className={TIER_COLORS[tier]}>{tier}</Badge>
                      <p className="text-2xl font-bold text-gray-900 mt-2">{rate}%</p>
                      <p className="text-xs text-gray-500">Commission</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Tier upgrades are based on your total successful conversions. Contact support for details.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Payout Request Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-600" />
              Request Payout
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">₹{(dashboard?.pending_payout || 0).toLocaleString()}</p>
            </div>

            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                placeholder="Minimum ₹500"
                className="bg-gray-50 border-gray-300"
              />
            </div>

            <div>
              <Label>Payout Method</Label>
              <Select value={payoutForm.payout_method} onValueChange={(v) => setPayoutForm({ ...payoutForm, payout_method: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                placeholder="Any special instructions..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowPayoutModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRequestPayout}
                disabled={payoutLoading || !payoutForm.amount}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {payoutLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Request Payout"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Commission Celebration Popup */}
      <Dialog open={showCommissionPopup} onOpenChange={setShowCommissionPopup}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 text-center max-w-md">
          <div className="py-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <PartyPopper className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Congratulations!</h2>
            <p className="text-gray-600 mb-6">You just earned a commission!</p>
            
            {newCommission && (
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white mb-6">
                <p className="text-sm text-green-100 mb-1">Commission Earned</p>
                <p className="text-4xl font-bold mb-2">₹{newCommission.commission_amount?.toLocaleString()}</p>
                <p className="text-sm text-green-100">
                  {newCommission.commission_rate}% from {newCommission.tenant_name}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 text-center mb-6">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-900">₹{(dashboard?.total_earnings || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Earnings</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-gray-900">₹{(dashboard?.pending_payout || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Available Balance</p>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowCommissionPopup(false)}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              Keep Growing!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
